import { useAuth } from "../context/AuthContext";
import React, { useState, useEffect } from "react";
import { FileDown, MessageCircle, Send, Phone, Check } from "lucide-react";
import { downloadWithAuth } from "../utils/download";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/* ── WhatsApp bulk sender panel ── */
function WhatsAppPanel({ authHeaders }) {
  const [guests, setGuests]     = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage]   = useState("");
  const [loading, setLoading]   = useState(false);
  const [loaded, setLoaded]     = useState(false);
  const [sent, setSent]         = useState(false);

  async function loadGuests() {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/guests/phones`, { headers: authHeaders() });
      const json = await res.json();
      setGuests(json.data || []);
      setLoaded(true);
    } catch {}
    setLoading(false);
  }

  function toggleGuest(id) {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }

  function selectAll() {
    setSelected(guests.map(g => g._id));
  }

  // Build wa.me deep-link (for manual sending if API not configured)
  function openWhatsApp(phone, msg) {
    const clean = phone.replace(/\D/g, "");
    const url   = `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-5">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center">
          <MessageCircle size={18} className="text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Bulk WhatsApp Outreach</h3>
          <p className="text-xs text-gray-400">Send promotions to past guests</p>
        </div>
      </div>

      {/* Message composer */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          Message Template
        </label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={4}
          placeholder="Hi {name}, hope you enjoyed your stay with us! 🏡&#10;We have exciting offers for your next visit."
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm
            focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
        />
        <p className="text-xs text-gray-400 mt-1">
          Use <code className="bg-gray-100 px-1 rounded">{"{name}"}</code> to personalize.
        </p>
      </div>

      {/* Guest list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-medium text-gray-500">
            Select Recipients
          </label>
          {!loaded ? (
            <button
              onClick={loadGuests}
              disabled={loading}
              className="text-xs font-medium text-green-600 hover:text-green-700"
            >
              {loading ? "Loading…" : "Load guest numbers"}
            </button>
          ) : (
            <button onClick={selectAll}
              className="text-xs font-medium text-green-600 hover:text-green-700">
              Select all ({guests.length})
            </button>
          )}
        </div>

        {loaded && (
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {guests.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                No guests with WhatsApp numbers found
              </p>
            ) : guests.map(g => {
              const isSelected = selected.includes(g._id);
              const name = g.fullNames?.[0] || "Guest";
              return (
                <div
                  key={g._id}
                  onClick={() => toggleGuest(g._id)}
                  className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer
                    transition-all ${isSelected
                      ? "bg-green-50 border border-green-200"
                      : "border border-transparent hover:bg-gray-50"
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center
                    flex-shrink-0 transition-colors
                    ${isSelected ? "bg-green-500 border-green-500" : "border-gray-300"}`}>
                    {isSelected && <Check size={11} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                    <p className="text-xs text-gray-400">{g.whatsapp}</p>
                  </div>
                  {/* Quick single send */}
                  {message && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openWhatsApp(
                          g.whatsapp,
                          message.replace(/\{name\}/g, name)
                        );
                      }}
                      className="p-1.5 hover:bg-green-100 rounded-lg transition-colors"
                      title="Send now"
                    >
                      <Send size={12} className="text-green-600" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action */}
      {loaded && selected.length > 0 && message && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
          <p className="text-sm text-green-700">
            Send to <strong>{selected.length}</strong> guests via WhatsApp
          </p>
          <button
            onClick={() => {
              // Open each in new tab (requires popup permissions) or integrate API
              selected.slice(0, 5).forEach(id => {
                const g = guests.find(g => g._id === id);
                if (g) openWhatsApp(
                  g.whatsapp,
                  message.replace(/\{name\}/g, g.fullNames?.[0] || "Guest")
                );
              });
              if (selected.length > 5) {
                alert(`Opening first 5 chats. For bulk sending, connect Twilio/Meta Cloud API.`);
              }
              setSent(true);
            }}
            className="flex items-center gap-1.5 text-sm font-medium text-white
              bg-green-500 hover:bg-green-600 px-3 py-1.5 rounded-lg transition-colors"
          >
            <MessageCircle size={14} /> Send
          </button>
        </div>
      )}

      {sent && (
        <p className="text-xs text-green-600 text-center">
          ✓ WhatsApp chats opened. For full bulk API, configure Twilio or Meta Cloud API in backend.
        </p>
      )}
    </div>
  );
}

/* ── Report generator ── */
export default function ReportsPage() {
  const { authHeaders } = useAuth();
  const [from, setFrom]     = useState("");
  const [to, setTo]         = useState("");
  const [source, setSource] = useState("");
  const [exportError, setExportError] = useState("");

  function csvUrl() {
    const params = new URLSearchParams();
    if (from)   params.append("from", from);
    if (to)     params.append("to", to);
    if (source) params.append("source", source);
    return `${API}/reports/csv?${params}`;
  }

  async function downloadCsv() {
    setExportError("");
    try { await downloadWithAuth(csvUrl(), authHeaders, "bookings.csv"); }
    catch (e) { setExportError(e.message); }
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports & Exports</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Download booking data and manage guest outreach
        </p>
      </div>

      {/* CSV Report */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center">
            <FileDown size={18} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">CSV Report</h3>
            <p className="text-xs text-gray-400">
              Export all booking data — replaces your Google Sheets workflow
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Platform</label>
            <select value={source} onChange={e => setSource(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
              <option value="">All Platforms</option>
              {["Website","Airbnb","Booking.com","Agoda","MakeMyTrip","Manual"].map(s =>
                <option key={s}>{s}</option>
              )}
            </select>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs font-medium text-gray-500 mb-1">Included fields:</p>
          <p className="text-xs text-gray-400">
            Timestamp · Booking Ref · Guest Names · Check-in · Check-out ·
            Travel From · Travel To · Purpose · Source · WhatsApp · ID Proof URL ·
            Total Amount · Advance · Balance · Payment Status
          </p>
        </div>

        <button
          onClick={downloadCsv}
          className="tap-44 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
            bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-medium
            hover:from-blue-600 hover:to-indigo-600 shadow-sm transition-all"
        >
          <FileDown size={16} /> Download CSV
        </button>
        {exportError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{exportError}</p>}
      </div>

      {/* WhatsApp */}
      <WhatsAppPanel authHeaders={authHeaders} />
    </div>
  );
}
